/* eslint-disable @typescript-eslint/no-floating-promises */
import { RangeSlider } from '@mantine/core';
import React from 'react';
import { AssetDownscaleStatuses } from '../../../../../common/src/domain/asset/assetDownscaleStatuses';
import { InspectionStandardTypes } from '../../../../../common/src/enums/InspectionStandardTypes';
import { ITranslateProps, translate } from '../../../mixins/translate';
import { fetchDelete, fetchGet, fetchPost } from '../../../store/fetch';
import { AssetTypes as assetTypes } from '../../../utils/enums/AssetTypes';
import { formatTimestamp } from '../../../utils/formatTimestamp';
import { Panorama360VideoPlayer } from '../../Panorama360VideoPlayer';
import { Spinner } from '../../spinners';
import { Component } from '../../utils/Component';
import { DisplayMessage } from '../../utils/displayMessage';
import { IAsset } from '../Interfaces/IAsset';
import { IElement } from '../Interfaces/IElement';
import { IUnit } from '../Interfaces/IUnit';

interface ISurveyVideoDetailsProps extends ITranslateProps {
    id: string;
    name: string;
    objectId: string;
    surveyId: string;
    isAssetDescriptionVisible?: boolean | undefined;
    elements: IElement[];
    downscaleStatusHigh: string;
    downscaleStatusLow: string;
    inspectionStandardType: string;
    size: number;
    isPanorama?: boolean | undefined;
    duration?: number | null | undefined;
}

interface ISurveyVideoDetailsState {
    submitting: boolean;
    generating: boolean;
    assets: IAsset[];
    selectedCategoryId: string;
    selectedAssetId: string;
    selectedElementId?: undefined | string | null;
    selectedUnitId?: undefined | string | null;
    selectedManifestationId?: undefined | string | null;
    videoCurrentTime: number;
    videoCanPlay: boolean;
    videoDuration: number;
    fragmentRangeStart?: undefined | number;
    fragmentRangeEnd?: undefined | number;
    isPlayingFragment: boolean;
    isSlowMotion: boolean;
    useHighBitrate: boolean;
    downscaleStatusHigh?: undefined | string;
    downscaleStatusLow?: undefined | string;
}

class SurveyVideoDetails extends Component<ISurveyVideoDetailsProps, ISurveyVideoDetailsState> {
    private videoElementHigh?: undefined | HTMLVideoElement;
    private videoElementLow?: undefined | HTMLVideoElement;

    constructor(props: ISurveyVideoDetailsProps) {
        super(props);

        // For panorama videos we don't need to wait for the PSV-internal
        // <video> element to report `loadeddata` — we already know the duration
        // from the asset record and can make the snippet/still toolbar visible
        // immediately once the downscale is done.
        const isPanoramaReady =
            props.isPanorama === true &&
            props.downscaleStatusHigh === AssetDownscaleStatuses.Complete &&
            props.downscaleStatusLow === AssetDownscaleStatuses.Complete;
        const initialDuration = isPanoramaReady ? Math.floor(props.duration ?? 0) : 0;

        this.state = {
            assets: [],
            selectedCategoryId: '0',
            selectedAssetId: '0',
            videoCurrentTime: 0,
            videoCanPlay: isPanoramaReady,
            videoDuration: initialDuration,
            fragmentRangeStart: 0,
            fragmentRangeEnd: initialDuration,
            isPlayingFragment: false,
            isSlowMotion: false,
            submitting: false,
            generating: false,
            useHighBitrate: true,
            downscaleStatusLow: props.downscaleStatusLow,
            downscaleStatusHigh: props.downscaleStatusHigh,
        };
    }

    public override async componentDidMount() {
        if (
            this.props.downscaleStatusLow !== AssetDownscaleStatuses.Complete ||
            this.props.downscaleStatusHigh !== AssetDownscaleStatuses.Complete ||
            this.props.downscaleStatusLow !== AssetDownscaleStatuses.Complete
        ) {
            await this.loadDownscaleStatus();
        } else {
            await this.loadAssets();
        }
    }

    public override render() {
        const isCategoriesAvailable = this.getCategories().length > 1;
        const isVideoTooLarge = this.isVideoTooLarge();

        if (
            this.state.downscaleStatusLow === undefined ||
            this.state.downscaleStatusLow === null ||
            this.state.downscaleStatusHigh === undefined ||
            this.state.downscaleStatusHigh === null
        ) {
            return <div className="mt10"> &nbsp; </div>;
        }

        if (this.state.downscaleStatusLow === AssetDownscaleStatuses.Error || this.state.downscaleStatusHigh === AssetDownscaleStatuses.Error) {
            return (
                <div className="mt10">
                    <DisplayMessage
                        message={`${this.props.t('video.jobError')} ${this.state.downscaleStatusLow}-${this.state.downscaleStatusHigh}`}
                        messageType="error"
                    />
                </div>
            );
        }
        if (this.state.downscaleStatusLow === AssetDownscaleStatuses.Canceled || this.state.downscaleStatusHigh === AssetDownscaleStatuses.Canceled) {
            return (
                <div className="mt10">
                    <DisplayMessage
                        message={`${this.props.t('video.jobCancelled')} ${this.state.downscaleStatusLow}-${this.state.downscaleStatusHigh}`}
                        messageType="warning"
                    />
                </div>
            );
        }

        if (
            (this.state.downscaleStatusLow && this.state.downscaleStatusLow !== AssetDownscaleStatuses.Complete) ||
            (this.state.downscaleStatusHigh && this.state.downscaleStatusHigh !== AssetDownscaleStatuses.Complete)
        ) {
            return (
                <div className="mt10">
                    <DisplayMessage
                        message={`${this.props.t('video.downscaling')} ${this.state.downscaleStatusLow}-${this.state.downscaleStatusHigh})`}
                        messageType="general"
                    />
                </div>
            );
        }

        return (
            <div className="ml30">
                <div style={{ position: 'relative' }}>
                    {this.state.generating && (
                        <div style={{ margin: '0 auto', width: '100%', height: '100%', position: 'absolute', opacity: 0.7, backgroundColor: '#FFF' }}>
                            <Spinner />
                        </div>
                    )}
                    {this.props.isPanorama ? (
                        <Panorama360VideoPlayer
                            videoId="videohigh"
                            height="500px"
                            src={`/api/assets/${this.props.id}/download${this.getDownScaledUrl('high')}`}
                            onVideoElement={this.attachPanoramaVideoElement}
                        />
                    ) : (
                        <>
                            <video
                                id={'videolow'}
                                className={this.state.useHighBitrate ? 'hidden' : ''}
                                onLoadedData={(e) => {
                                    this.videoOnLoadedDataLow(e.currentTarget);
                                }}
                                onTimeUpdate={(e) => {
                                    this.videoOnTimeUpdateLow(e.currentTarget);
                                }}
                                crossOrigin="use-credentials"
                                preload="auto"
                                controls={true}
                                width="100%"
                            >
                                <source src={`/api/assets/${this.props.id}/download${this.getDownScaledUrl('low')}`} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                            <video
                                id={'videohigh'}
                                className={this.state.useHighBitrate ? '' : 'hidden'}
                                onLoadedData={(e) => {
                                    this.videoOnLoadedDataHigh(e.currentTarget);
                                }}
                                onTimeUpdate={(e) => {
                                    this.videoOnTimeUpdateHigh(e.currentTarget);
                                }}
                                crossOrigin="use-credentials"
                                preload="auto"
                                controls={true}
                                width="100%"
                            >
                                <source src={`/api/assets/${this.props.id}/download${this.getDownScaledUrl('high')}`} type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        </>
                    )}
                </div>

                <br />

                <div className={this.state.videoCanPlay ? 'hidden' : ''}>Loading...</div>
                <div className={this.state.videoCanPlay ? '' : 'hidden'}>
                    <div>
                        <button
                            disabled={this.state.submitting || this.state.generating}
                            className={this.state.isSlowMotion ? 'hidden' : 'btn btn-default btn-sm mr10 fs12'}
                            onClick={() => this.onPlaybackRateChanged(0.5)}
                        >
                            {' '}
                            <span className="fa fa-clock-o pr5"></span> Slowmotion
                        </button>
                        <button
                            disabled={this.state.submitting || this.state.generating}
                            className={this.state.isSlowMotion ? 'btn btn-success btn-sm mr10 fs12' : 'hidden'}
                            onClick={() => this.onPlaybackRateChanged(1)}
                        >
                            {' '}
                            <span className="fa fa-clock-o pr5"></span> Slowmotion
                        </button>
                        {!this.props.isPanorama && (
                            <>
                                <button
                                    disabled={this.state.submitting || this.state.generating}
                                    className={this.state.useHighBitrate ? 'hidden' : 'btn btn-default btn-sm mr10 fs12'}
                                    onClick={() => this.handleBitRate()}
                                >
                                    {' '}
                                    <span className="fa fa-star pr5"></span> High quality
                                </button>
                                <button
                                    disabled={this.state.submitting || this.state.generating}
                                    className={this.state.useHighBitrate ? 'btn btn-success btn-sm mr10 fs12' : 'hidden'}
                                    onClick={() => this.handleBitRate()}
                                >
                                    {' '}
                                    <span className="fa fa-star-half-full pr5"></span> High quality
                                </button>
                            </>
                        )}
                        <button
                            disabled={this.state.submitting || this.state.generating}
                            className="btn btn-primary btn-sm mr10 fs12"
                            onClick={() => this.onFrameByFrame('backward')}
                        >
                            {' '}
                            <span className="fa fa-step-backward"></span>
                        </button>
                        <button
                            disabled={this.state.submitting || this.state.generating}
                            className="btn btn-primary btn-sm mr10 fs12"
                            onClick={() => this.onFrameByFrame('forward')}
                        >
                            {' '}
                            <span className="fa fa-step-forward"></span>
                        </button>
                        <button
                            disabled={this.state.submitting || this.state.generating}
                            className="btn btn-primary btn-sm mr10 fs12"
                            onClick={() => this.onVideoSetVideoJump(-1)}
                        >
                            {' '}
                            <span className="fa fa-undo pr5"></span>1
                        </button>
                        <button
                            disabled={this.state.submitting || this.state.generating}
                            className="btn btn-primary btn-sm mr10 fs12"
                            onClick={() => this.onVideoSetVideoJump(-5)}
                        >
                            {' '}
                            <span className="fa fa-undo pr5"></span>5
                        </button>
                        <button
                            disabled={this.state.submitting || this.state.generating}
                            className="btn btn-primary btn-sm mr10 fs12"
                            onClick={() => this.onVideoSetVideoJump(1)}
                        >
                            {' '}
                            <span className="fa fa-repeat pr5"></span>1
                        </button>
                        <button
                            disabled={this.state.submitting || this.state.generating}
                            className="btn btn-primary btn-sm mr10 fs12"
                            onClick={() => this.onVideoSetVideoJump(5)}
                        >
                            {' '}
                            <span className="fa fa-repeat pr5"></span>5
                        </button>
                    </div>
                    <canvas id="canvas" width="30" height="30" className="hidden"></canvas>

                    {isVideoTooLarge ? (
                        <div className="mt40">
                            <DisplayMessage
                                message="Videos larger than 950 MB cannot be edited or used for still image extraction"
                                messageType="warning"
                            />
                        </div>
                    ) : (
                        <>
                            <div className="section-divider mt40 mb50">
                                <span> {this.props.t('video.create')} </span>
                            </div>
                            <div className="mt20">
                                <RangeSlider
                                    min={0}
                                    max={this.state.videoDuration}
                                    minRange={0}
                                    value={[this.state.fragmentRangeStart ?? 0, this.state.fragmentRangeEnd ?? 0]}
                                    label={(value) => formatTimestamp(value)}
                                    labelAlwaysOn
                                    onChange={(value) => void this.onFragmentRangeChanged(value)}
                                />
                            </div>
                            <div className="text-right mt20">
                                <button
                                    className="btn btn-primary btn-sm mr10 fs13"
                                    disabled={this.state.submitting || this.state.generating}
                                    onClick={async () => this.playFragment()}
                                >
                                    {' '}
                                    <span className="fa fa-play pr5"></span> {this.props.t('video.playFragment')}
                                </button>
                                <button
                                    className="btn btn-primary btn-sm mr10 fs13"
                                    disabled={this.state.submitting || this.state.generating}
                                    id="snap"
                                    onClick={async () => await this.saveFragment()}
                                >
                                    {' '}
                                    <span className="fa fa-film pr5"></span> {this.props.t('video.saveFragment')}
                                </button>
                                <button
                                    className="btn btn-primary btn-sm fs13"
                                    disabled={this.state.submitting || this.state.generating}
                                    id="snap"
                                    onClick={async () => await this.snap()}
                                >
                                    {' '}
                                    <span className="fa fa-image pr5"></span> {this.props.t('video.saveStill')}
                                </button>
                            </div>
                        </>
                    )}
                </div>

                <div className={this.state.assets.length === 0 ? 'hidden' : ''}>
                    <div className="section-divider mt40">
                        <span>{this.props.t('video.saved')}</span>
                    </div>

                    <div className="panel">
                        <table className="table pr10 br-b">
                            <thead>
                                <tr className="bg-light dark">
                                    <th className="">&nbsp;</th>
                                    <th className="">{this.props.t('common.title')}</th>
                                    <th className="">{this.props.t('common.location')}</th>
                                    <th className="">&nbsp; </th>
                                </tr>
                            </thead>
                            <tbody className="bg-light">
                                {this.state.assets.map((asset) => (
                                    <tr key={asset.id}>
                                        <td className="text-left">
                                            {asset.type === assetTypes.DerivedVideo && (
                                                <span>
                                                    <span className="fa fa-film mr10" />
                                                    {formatTimestamp(asset.derivedStartTimestamp)}- {formatTimestamp(asset.derivedEndTimestamp)}
                                                </span>
                                            )}
                                            {(asset.type === assetTypes.DerivedImage || asset.type === assetTypes.ImageAnnotation) && (
                                                <span>
                                                    <span className="fa fa-image mr10" />
                                                    {formatTimestamp(asset.derivedStartTimestamp)}
                                                </span>
                                            )}
                                        </td>

                                        <td className="text-left">
                                            <input
                                                type="text"
                                                name="titleClip"
                                                id="titleClip"
                                                className="gui-input fs13"
                                                placeholder={this.props.t('video.titleInput')}
                                                defaultValue={asset.name}
                                                onBlur={async (e) => await this.updateAssetNameDescription(e, asset.id, 'name')}
                                            />

                                            {this.props.isAssetDescriptionVisible && (
                                                <input
                                                    type="text"
                                                    name="descriptionClip"
                                                    id="descriptionClip"
                                                    className="gui-input fs13"
                                                    placeholder={this.props.t('common.description')}
                                                    defaultValue={asset.description ?? ''}
                                                    onBlur={async (e) => await this.updateAssetNameDescription(e, asset.id, 'description')}
                                                />
                                            )}
                                        </td>

                                        <td className="text-left">
                                            {!asset.elementId && this.state.selectedElementId && !this.getCategoryId(asset.elementId, asset.id) && (
                                                <div className="p10">
                                                    <button
                                                        disabled={this.state.submitting}
                                                        type="button"
                                                        onClick={async () => this.copyAssetAsignment(asset.id)}
                                                        className="btn btn-default fs12 btn-xs"
                                                    >
                                                        {' '}
                                                        <i className="fa fa-copy"></i> {this.props.t('video.copySelection')}
                                                    </button>
                                                </div>
                                            )}

                                            {
                                                /* Category selection */
                                                isCategoriesAvailable && (
                                                    <div>
                                                        <select
                                                            value={
                                                                this.getCategoryId(asset.elementId, asset.id)
                                                                    ? this.getCategoryId(asset.elementId, asset.id)
                                                                    : '-1'
                                                            }
                                                            onChange={async (e) => {
                                                                await this.setState({
                                                                    selectedCategoryId: e.target.value,
                                                                    selectedAssetId: asset.id,
                                                                });
                                                            }}
                                                            className="form-control fs13"
                                                        >
                                                            <option value="-1">
                                                                {this.props.t('common.selectItem', this.props.t('common.category'))}
                                                            </option>
                                                            {this.getCategories().map((category) => (
                                                                <option key={category.categoryId} value={category.categoryId}>
                                                                    {category.categoryName}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )
                                            }
                                            {
                                                /* Element selection */
                                                isCategoriesAvailable &&
                                                    this.getElements(this.getCategoryId(asset.elementId, asset.id)).length > 0 && (
                                                        <div>
                                                            <select
                                                                value={asset.elementId ? asset.elementId : '-1'}
                                                                onChange={(e) => this.onElementChange(e, asset.id)}
                                                                className="form-control fs13"
                                                            >
                                                                <option value="-1">
                                                                    {this.props.t('common.selectItem', this.props.t('common.element'))}
                                                                </option>
                                                                {this.getElements(this.getCategoryId(asset.elementId, asset.id)).map((element) => (
                                                                    <option key={element.id} value={element.id}>
                                                                        {this.getCategoryId(asset.elementId, asset.id) === '0'
                                                                            ? `${element.categoryName} - `
                                                                            : ''}
                                                                        {element.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    )
                                            }
                                            {!isCategoriesAvailable && (
                                                <div>
                                                    <select
                                                        value={asset.elementId ? asset.elementId : '-1'}
                                                        onChange={(e) => this.onElementChange(e, asset.id)}
                                                        className="form-control fs13"
                                                    >
                                                        <option value="-1">
                                                            {this.props.t('common.selectItem', this.props.t('common.element'))}
                                                        </option>
                                                        {this.props.elements
                                                            .filter((element) => !element.isArchived)
                                                            .map((element) => (
                                                                <option key={element.id} value={element.id}>
                                                                    {element.name}
                                                                </option>
                                                            ))}
                                                    </select>
                                                </div>
                                            )}
                                            {
                                                /* Unit selection */
                                                this.getUnits(asset.elementId).length > 0 && (
                                                    <div>
                                                        <select
                                                            value={asset.unitId ? asset.unitId : '-1'}
                                                            onChange={(e) => this.onUnitChange(e, asset.id)}
                                                            className="form-control  fs13"
                                                        >
                                                            <option value="-1">
                                                                {this.props.t('common.selectItem', this.props.t('common.unit'))}
                                                            </option>
                                                            {this.getUnits(asset.elementId).map((unit) => (
                                                                <option key={unit.id} value={unit.id}>
                                                                    {unit.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )
                                            }
                                            {
                                                /* Manifistation selection */
                                                this.getManifestations(asset.elementId, asset.unitId).length > 0 && (
                                                    <div>
                                                        <select
                                                            value={asset.manifestationId ? asset.manifestationId : '-1'}
                                                            onChange={(e) => this.onManifestationChange(e, asset.id)}
                                                            className="form-control  fs13"
                                                        >
                                                            <option value="-1">
                                                                {this.props.t('common.selectItem', this.props.t('common.manifestation'))}
                                                            </option>
                                                            {this.getManifestations(asset.elementId, asset.unitId).map((manifestation) => (
                                                                <option key={manifestation.id} value={manifestation.id}>
                                                                    {manifestation.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                )
                                            }
                                        </td>
                                        <td className="text-right" style={{ whiteSpace: 'nowrap' }}>
                                            <span className={asset.elementId ? '' : 'hidden'}>
                                                <a className="btn btn-default btn-xs pv2 mr10" href={this.getLinkToLocation(asset)}>
                                                    <span className="fa fa-search"></span>
                                                </a>
                                                {/* <Link className="btn btn-default btn-xs pv2 mr10" to={this.getLinkToLocation(asset)}><i className="fa fa-search"></i></Link> */}
                                            </span>

                                            <button
                                                disabled={this.state.submitting}
                                                type="button"
                                                onClick={async (e) => this.showDerivedAsset(e, asset)}
                                                className="btn btn-default btn-xs mr10"
                                                title="Open in player"
                                            >
                                                {' '}
                                                <i className="fa fa-play"></i>{' '}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    if (window.confirm(this.props.t('common.confirmDeleteItem'))) {
                                                        this.remove(e, asset.id);
                                                    }
                                                }}
                                                className="btn btn-default btn-xs"
                                            >
                                                {' '}
                                                <i className="fa fa-trash-o"></i>{' '}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    private attachPanoramaVideoElement = (videoNode: HTMLVideoElement) => {
        if (this.videoElementHigh === videoNode) {
            return;
        }
        this.videoElementHigh = videoNode;
        // The 360 viewer renders one source; the bitrate-switch UI is hidden, so
        // point the low-bitrate handle at the same element to keep snap/fragment
        // logic happy.
        this.videoElementLow = videoNode;
        videoNode.addEventListener('loadeddata', () => this.videoOnLoadedDataHigh(videoNode));
        videoNode.addEventListener('timeupdate', () => this.videoOnTimeUpdateHigh(videoNode));
        // PSV initialises the <video> element before firing its `ready` event,
        // so `loadedmetadata` / `loadeddata` may have fired before we got the
        // chance to subscribe. Without this fall-through the snippet + still
        // toolbar stays stuck on "Loading…".
        if (videoNode.readyState >= 1 /* HAVE_METADATA */) {
            this.videoOnLoadedDataHigh(videoNode);
        }
    };

    private isVideoTooLarge(): boolean {
        // 800 MB in bytes
        const maxSize = 950 * 1024 * 1024;
        return this.props.size > maxSize;
    }

    private getLinkToLocation(asset: {
        elementId?: undefined | string | null;
        unitId?: undefined | string | null;
        manifestationId?: undefined | string | null;
    }) {
        let tabName: string;
        if (this.props.inspectionStandardType === InspectionStandardTypes.Fmeca) {
            tabName = 'analysis';
        } else if (this.props.inspectionStandardType === InspectionStandardTypes.Memo) {
            tabName = 'summaryAndAdvice';
        } else if (this.props.inspectionStandardType === InspectionStandardTypes.Model3d) {
            tabName = 'pointcloud';
        } else if (
            [
                InspectionStandardTypes.Free,
                InspectionStandardTypes.Nen2767,
                InspectionStandardTypes.Api653,
                InspectionStandardTypes.Api570,
                InspectionStandardTypes.Eemua159,
            ].includes(this.props.inspectionStandardType as InspectionStandardTypes)
        ) {
            tabName = 'conditionAssessment';
        } else {
            tabName = 'editReport';
        }
        return `/surveys/${this.props.surveyId}/${tabName}?elementId=${asset.elementId}${asset.unitId ? `&unitId=${asset.unitId}` : ''}${
            asset.manifestationId ? `&manifestationId=${asset.manifestationId}` : ''
        }`;
    }

    private async copyAssetAsignment(assetId: string) {
        await this.updateAsset(assetId, this.state.selectedElementId, this.state.selectedUnitId, this.state.selectedManifestationId);
        await this.loadAssets();
    }

    private async updateAssetNameDescription(e: any, assetId: string, fieldName: string) {
        let data: any;
        if (fieldName === 'name') {
            data = { name: e.target.value };
        } else {
            data = { description: e.target.value };
        }
        const url = `/api/surveys/${this.props.surveyId}/assets/${assetId}`;
        await fetchPost(url, {}, data);
    }

    private async loadDownscaleStatus() {
        const url = `/api/assets/${this.props.id}/downscaleStatus`;
        const result = await fetchGet(url);
        this.setState({ downscaleStatusLow: result.json.downscaleStatusLow, downscaleStatusHigh: result.json.downscaleStatusHigh });
        if (
            result.json.downscaleStatusLow === AssetDownscaleStatuses.Complete &&
            result.json.downscaleStatusHigh === AssetDownscaleStatuses.Complete
        ) {
            await this.loadAssets();
        } else {
            setTimeout(async () => this.loadDownscaleStatus(), 60000);
        }
    }

    private handleBitRate() {
        if (!this.videoElementHigh || !this.videoElementLow) {
            return;
        }
        if (this.state.useHighBitrate) {
            if (this.videoElementHigh) {
                this.videoElementLow.pause();
                this.videoElementHigh.currentTime = this.videoElementLow.currentTime;
            }
            this.setState({ useHighBitrate: false });
        } else {
            if (this.videoElementLow) {
                this.videoElementHigh.pause();
                this.videoElementLow.currentTime = this.videoElementHigh.currentTime;
            }
            this.setState({ useHighBitrate: true });
        }
    }

    private async showDerivedAsset(e: React.FormEvent<any>, asset: IAsset) {
        e.preventDefault();
        if (!this.videoElementHigh || !this.videoElementLow) {
            return;
        }

        if (this.state.useHighBitrate) {
            this.videoElementHigh.currentTime = asset.derivedStartTimestamp ?? 0;
            await this.videoElementHigh.play();
            if (this.videoElementHigh.readyState > 2) {
                this.videoElementHigh.pause();
            }
        } else {
            this.videoElementLow.currentTime = asset.derivedStartTimestamp ?? 0;
            await this.videoElementLow.play();
            if (this.videoElementLow.readyState > 2) {
                this.videoElementLow.pause();
            }
        }

        if (asset.type === assetTypes.DerivedVideo) {
            await this.setState({ fragmentRangeStart: asset.derivedStartTimestamp, fragmentRangeEnd: asset.derivedEndTimestamp });
            if (this.state.useHighBitrate) {
                await this.videoElementHigh.play();
            } else {
                await this.videoElementLow.play();
            }
        }
    }

    private onElementChange(e: React.ChangeEvent<HTMLSelectElement>, assetId: string) {
        let elementId = e.target.value ? e.target.value : null;
        if (elementId === '-1') {
            elementId = null;
        }
        this.setState({ selectedElementId: elementId, selectedUnitId: undefined, selectedManifestationId: undefined });
        this.updateAsset(assetId, elementId, undefined, undefined);
    }

    private onUnitChange(e: React.ChangeEvent<HTMLSelectElement>, assetId: string) {
        let unitId = e.target.value ? e.target.value : null;

        if (unitId === '-1') {
            unitId = null;
        }
        this.setState({ selectedUnitId: unitId, selectedManifestationId: undefined });
        this.updateAsset(assetId, this.state.selectedElementId, unitId, undefined);
    }

    private onManifestationChange(e: React.ChangeEvent<HTMLSelectElement>, assetId: string) {
        let manifestationId = e.target.value ? e.target.value : null;

        if (manifestationId === '-1') {
            manifestationId = null;
        }
        this.setState({ selectedManifestationId: manifestationId });
        this.updateAsset(assetId, this.state.selectedElementId, this.state.selectedUnitId, manifestationId);
    }

    private getCategoryId(elementId?: string | null, assetId?: string) {
        if (this.state.selectedCategoryId !== '0' && this.state.selectedAssetId === assetId) {
            return this.state.selectedCategoryId;
        }
        if (!elementId) {
            return undefined;
        }
        const selectedElements = this.props.elements.filter((element: IElement) => element.id === elementId);
        const selectedElement = selectedElements.length === 1 ? selectedElements[0] : undefined;

        if (!selectedElement) {
            return undefined;
        }
        return selectedElement.categoryId;
    }

    private getCategories() {
        interface ITempObject {
            [key: string]: boolean;
        }

        const categories: ITempObject = {};
        const categoryList = this.props.elements.filter((entry) => {
            if (categories[entry.categoryId]) {
                return false;
            }
            categories[entry.categoryId] = true;
            return true;
        });

        return categoryList;
    }

    private getElements(categoryId?: string) {
        if (!categoryId) {
            return [];
        }
        if (categoryId === '0') {
            return this.props.elements;
        }
        const selectedElements = this.props.elements.filter((element: IElement) => element.categoryId === categoryId);

        return selectedElements;
    }

    private getUnits(elementId?: string | null) {
        if (!elementId) {
            return [];
        }
        const selectedElements = this.props.elements.filter((element: IElement) => element.id === elementId);
        const selectedElement = selectedElements.length === 1 ? selectedElements[0] : undefined;

        if (!selectedElement) {
            return [];
        }
        const activeUnits = selectedElement.units.filter((unit) => !unit.isArchived);
        return activeUnits;
    }

    private getManifestations(elementId?: string | null, unitId?: string | null) {
        if (!elementId) {
            return [];
        }
        if (!unitId) {
            return [];
        }
        const element = this.props.elements.find((i) => i.id === elementId);

        if (!element) {
            return [];
        }

        const selectedUnits = element.units.filter((unit: IUnit) => unit.id === unitId);
        const selectedUnit = selectedUnits.length === 1 ? selectedUnits[0] : undefined;

        if (!selectedUnit) {
            return [];
        }

        return selectedUnit.manifestations;
    }

    private getDownScaledUrl(quality: string) {
        if (quality === 'high') {
            if (this.state.downscaleStatusHigh === AssetDownscaleStatuses.Complete) {
                return '?downscaledHigh=true';
            }
            return '?downscaled=false';
        }
        return '?downscaled=true';
    }

    private onPlaybackRateChanged(value: number) {
        if (!this.videoElementHigh || !this.videoElementLow) {
            return;
        }
        if (value === 1) {
            this.setState({ isSlowMotion: false });
        } else {
            this.setState({ isSlowMotion: true });
        }

        this.videoElementHigh.playbackRate = value;
        this.videoElementLow.playbackRate = value;
    }

    private onFrameByFrame(direction: string) {
        if (!this.videoElementHigh || !this.videoElementLow) {
            return;
        }
        const frameTime = 1 / 30;

        if (!this.videoElementHigh.paused) {
            this.videoElementHigh.pause();
        }
        if (!this.videoElementLow.paused) {
            this.videoElementLow.pause();
        }

        if (direction === 'forward') {
            if (this.videoElementHigh.currentTime < this.videoElementHigh.duration) {
                if (this.state.useHighBitrate) {
                    this.videoElementHigh.currentTime = Math.min(this.videoElementHigh.duration, this.videoElementHigh.currentTime + frameTime);
                    this.videoElementLow.currentTime = Math.min(this.videoElementHigh.duration, this.videoElementHigh.currentTime + frameTime);
                } else {
                    this.videoElementHigh.currentTime = Math.min(this.videoElementLow.duration, this.videoElementLow.currentTime + frameTime);
                    this.videoElementLow.currentTime = Math.min(this.videoElementLow.duration, this.videoElementLow.currentTime + frameTime);
                }
            }
        } else {
            if (this.videoElementHigh.currentTime > 0) {
                this.videoElementHigh.currentTime -= frameTime;
                this.videoElementLow.currentTime -= frameTime;
            }
        }
    }

    private async updateAsset(assetId: string, elementId?: string | null, unitId?: string | null, manifestationId?: string | null) {
        const data = { elementId, unitId, isInConditionReport: true, manifestationId };
        const url = `/api/surveys/${this.props.surveyId}/assets/${assetId}`;
        await fetchPost(url, {}, data);

        const items = this.state.assets;
        const objIndex = this.state.assets.findIndex((asset) => asset.id === assetId);
        const asset = items[objIndex]!;
        if (elementId) {
            asset.elementId = elementId;
            asset.unitId = unitId;
            asset.manifestationId = manifestationId;
        } else if (unitId) {
            asset.unitId = unitId;
            asset.manifestationId = manifestationId;
        } else {
            asset.manifestationId = manifestationId;
        }
        await this.setState({ assets: items });
    }

    private async playFragment() {
        if (!this.videoElementHigh || !this.videoElementLow) {
            return;
        }
        if (this.state.useHighBitrate) {
            if (this.videoElementLow) {
                this.videoElementLow.pause();
            }
            this.videoElementHigh.currentTime = this.state.fragmentRangeStart ?? 0;
            // this.videoElementHigh.play();
            await this.videoElementHigh.play();
            // if (playPromise !== null) {
            //     playPromise.catch(() => { this.videoElementHigh.play(); });
            // }
        } else {
            if (this.videoElementHigh) {
                this.videoElementHigh.pause();
            }
            this.videoElementLow.currentTime = this.state.fragmentRangeStart ?? 0;
            // this.videoElementLow.play();
            await this.videoElementLow.play();
            // if (playPromise !== null) {
            //     playPromise.catch(() => { this.videoElementLow.play(); });
            // }
        }
        await this.setState({ isPlayingFragment: true });
    }

    private async saveFragment() {
        this.setState({ submitting: true, generating: true });
        // See snap(): for 360 videos the <video> is not in the DOM, so resolve
        // it through the element ref kept in sync by the player callback.
        const video = this.state.useHighBitrate ? this.videoElementHigh : this.videoElementLow;
        video?.pause();

        await this.uploadFragment(this.state.fragmentRangeStart ?? 0, this.state.fragmentRangeEnd ?? 0);

        this.setState({ generating: false });

        await this.loadAssets();
    }

    private async onFragmentRangeChanged(values: number[]) {
        if (!this.videoElementHigh || !this.videoElementLow) {
            return;
        }
        if (this.state.fragmentRangeStart !== values[0]) {
            this.videoElementHigh.currentTime = values[0]!;
            this.videoElementLow.currentTime = values[0]!;
        } else if (this.state.fragmentRangeEnd !== values[1]) {
            this.videoElementHigh.currentTime = values[1]!;
            this.videoElementLow.currentTime = values[1]!;
        }
        await this.setState({ fragmentRangeStart: values[0], fragmentRangeEnd: values[1] });
    }

    private videoOnLoadedDataHigh(video: HTMLVideoElement) {
        video.volume = 0; // default volume switched off
        this.videoElementHigh = video;
        if (this.videoElementLow) {
            const duration = Math.floor(video.duration);
            this.setState({
                videoCanPlay: true,
                videoDuration: duration,
                fragmentRangeEnd: duration,
            });
        }
    }

    private videoOnLoadedDataLow(video: HTMLVideoElement) {
        this.videoElementLow = video;
        video.volume = 0; // default volume switched off
        if (this.videoElementHigh) {
            const duration = Math.floor(video.duration);
            this.setState({
                videoCanPlay: true,
                videoDuration: duration,
                fragmentRangeEnd: duration,
            });
        }
    }

    private videoOnTimeUpdateHigh(video: HTMLVideoElement) {
        this.setState({ videoCurrentTime: video.currentTime });
        if (video.currentTime > (this.state.fragmentRangeEnd ?? 0)) {
            video.pause();
        }
    }

    private videoOnTimeUpdateLow(video: HTMLVideoElement) {
        this.setState({ videoCurrentTime: video.currentTime });
        if (video.currentTime > (this.state.fragmentRangeEnd ?? 0)) {
            video.pause();
        }
    }

    private onVideoSetVideoJump(value: number) {
        if (!this.videoElementHigh || !this.videoElementLow) {
            return;
        }
        if (this.videoElementHigh.currentTime + value > 0) {
            this.videoElementHigh.currentTime = this.videoElementHigh.currentTime + value;
            this.videoElementLow.currentTime = this.videoElementLow.currentTime + value;
            this.setState({ videoCurrentTime: this.videoElementHigh.currentTime + value });
        } else {
            this.videoElementHigh.currentTime = 0;
            this.videoElementLow.currentTime = 0;
            this.setState({ videoCurrentTime: 0 });
        }
    }

    private async snap() {
        this.setState({ submitting: true, generating: true });
        // Use the element refs instead of a DOM id lookup: for 360 videos the
        // <video> lives inside Photo Sphere Viewer as a WebGL texture source and
        // is never attached to the document, so querySelector would return null.
        const video = this.state.useHighBitrate ? this.videoElementHigh : this.videoElementLow;
        if (!video) {
            this.setState({ submitting: false, generating: false });
            return;
        }

        video.pause();
        const startTime = video.currentTime;

        await this.uploadStill(startTime);

        await this.setState({ generating: false });

        await this.loadAssets();
    }

    private async uploadStill(startTime: number) {
        const url = `/api/assets/still?surveyId=${this.props.surveyId}&objectId=${this.props.objectId}&derivedFromAssetId=${this.props.id}&type=${assetTypes.DerivedImage}&derivedStartTimestamp=${startTime}`;
        await fetchPost(url, {}, {});
    }

    private async uploadFragment(startTime: number, endTime: number) {
        await fetchPost(
            '/api/assets/fragment',
            {
                surveyId: this.props.surveyId,
                objectId: this.props.objectId,
                derivedFromAssetId: this.props.id,
                type: assetTypes.DerivedVideo,
                derivedStartTimestamp: startTime.toString(),
                derivedEndTimestamp: endTime.toString(),
            },
            {},
        );
    }

    private async loadAssets() {
        await this.setState({ submitting: true });

        const url = `/api/assets?surveyId=${this.props.surveyId}&derivedFromAssetId=${this.props.id}&orderby=assets.derivedStartTimestamp`;
        const result = await fetchGet(url);
        await this.setState({ submitting: false, assets: result.json });
    }

    private async remove(e: any, id: string) {
        e.preventDefault();
        await this.setState({ submitting: true });

        const url = `/api/surveys/${this.props.surveyId}/assets/${id}`;
        await fetchDelete(url);
        await this.loadAssets();
    }
}

// eslint-disable-next-line import/no-default-export
export default translate()(SurveyVideoDetails);
